import React, { useState, useCallback, useEffect } from 'react';
import { NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LayoutDashboard, Ticket, Users, LogOut, Menu, X, Settings, Mail, ChevronDown, ChevronRight, Building2, PanelLeftClose, PanelLeftOpen, BookOpen, Plus, Phone, PhoneIncoming, PhoneOutgoing, MessageSquare, FileText, ExternalLink, ShieldCheck, Monitor } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

const JOURNAL_TYPES = [
  { value: 'phone_in',      label: 'Anruf erhalten',        icon: PhoneIncoming  },
  { value: 'phone_out',     label: 'Anruf getaetigt',       icon: PhoneOutgoing  },
  { value: 'messenger_in',  label: 'Messenger erhalten',    icon: MessageSquare  },
  { value: 'messenger_out', label: 'Messenger gesendet',    icon: MessageSquare  },
  { value: 'email_in',      label: 'E-Mail erhalten',       icon: Mail           },
  { value: 'email_out',     label: 'E-Mail gesendet',       icon: Mail           },
  { value: 'personal',      label: 'Pers. Kontakt',         icon: Users          },
  { value: 'note',          label: 'Interne Notiz',         icon: FileText       },
];

function JournalPanel({ onClose }) {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState({ customer_id: '', contact_id: '', type: 'note', note: '', followup_at: '', device_id: '' });
  const [devices, setDevices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedEntry, setSavedEntry] = useState(null);
  const [convertingTicket, setConvertingTicket] = useState(false);

  useEffect(() => {
    api.get('/customers?limit=200').then(res => setCustomers(res.data.customers || res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.customer_id) { setContacts([]); return; }
    api.get('/contacts/customer/' + form.customer_id).then(res => setContacts(res.data || [])).catch(() => {});
  }, [form.customer_id]);

  useEffect(() => {
    if (!form.customer_id) { setDevices([]); return; }
    api.get('/devices?customer_id=' + form.customer_id).then(res => setDevices(res.data || [])).catch(() => setDevices([]));
  }, [form.customer_id]);

  const handleSave = async () => {
    if (!form.customer_id) { toast.error('Bitte Kunde auswaehlen'); return; }
    if (!form.note.trim()) { toast.error('Notiz erforderlich'); return; }
    setSaving(true);
    try {
      const res = await api.post('/journal', {
        customer_id: parseInt(form.customer_id),
        contact_id: form.contact_id ? parseInt(form.contact_id) : null,
        type: form.type,
        note: form.note,
        followup_at: form.followup_at || null,
        device_id: form.device_id ? parseInt(form.device_id) : null,
      });
      setSavedEntry(res.data);
      toast.success('Eintrag gespeichert');
    } catch (e) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!savedEntry) return;
    setConvertingTicket(true);
    try {
      const res = await api.post('/tickets', {
        customer_id: savedEntry.customer_id,
        contact_id: savedEntry.contact_id || null,
        title: '',
        description: savedEntry.note,
        _prefill: true,
      });
      const ticketId = res.data?.id;
      if (ticketId) {
        await api.patch('/journal/' + savedEntry.id + '/ticket', { ticket_id: ticketId });
      }
      toast.success('Ticket erstellt');
      onClose();
      navigate('/tickets/new?from_journal=' + savedEntry.id
        + '&customer_id=' + savedEntry.customer_id
        + (savedEntry.contact_id ? '&contact_id=' + savedEntry.contact_id : '')
        + '&note=' + encodeURIComponent(savedEntry.note));
    } catch (e) {
      toast.error('Fehler beim Erstellen');
    } finally {
      setConvertingTicket(false);
    }
  };

  const handleNewEntry = () => {
    setSavedEntry(null);
    setForm({ customer_id: form.customer_id, contact_id: '', type: 'note', note: '', followup_at: '', device_id: '' });
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full max-w-md h-full flex flex-col shadow-2xl">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen size={16} className="text-primary-500" />
            <h2 className="font-semibold text-gray-900 text-sm">Journal-Eintrag</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {!savedEntry ? (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Kunde *</label>
                <select value={form.customer_id} onChange={e => setForm({...form, customer_id: e.target.value, contact_id: ''})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="">Bitte auswaehlen...</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              {contacts.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Ansprechpartner</label>
                  <select value={form.contact_id} onChange={e => setForm({...form, contact_id: e.target.value})}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                    <option value="">Kein Ansprechpartner</option>
                    {contacts.filter(c => c.is_active).map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                  </select>
                </div>
              )}
              {devices.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Geraet (optional)</label>
                  <select value={form.device_id} onChange={e => setForm({...form, device_id: e.target.value})}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                    <option value="">Kein Geraet</option>
                    {devices.map(d => <option key={d.id} value={d.id}>{d.display_name || d.system_name || d.dns_name || ('Geraet ' + d.id)}</option>)}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Typ *</label>
                <div className="grid grid-cols-2 gap-2">
                  {JOURNAL_TYPES.map(t => {
                    const Icon = t.icon;
                    return (
                      <button key={t.value} onClick={() => setForm({...form, type: t.value})}
                        className={'flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ' +
                          (form.type === t.value
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300')}>
                        <Icon size={13} className="shrink-0" />
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notiz *</label>
                <textarea value={form.note} onChange={e => setForm({...form, note: e.target.value})}
                  rows={5} placeholder="Was ist passiert?"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none" />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Wiedervorlage</label>
                <input type="date" value={form.followup_at} onChange={e => setForm({...form, followup_at: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="text-sm font-medium text-green-800 mb-1">Eintrag gespeichert</p>
                <p className="text-xs text-green-600 whitespace-pre-wrap">{savedEntry.note}</p>
              </div>
              <p className="text-sm text-gray-600">Was moechtest du als naechstes tun?</p>
              <button onClick={handleCreateTicket} disabled={convertingTicket}
                className="w-full flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-4 py-3 rounded-xl transition-colors disabled:opacity-50">
                <Ticket size={16} />
                {convertingTicket ? 'Wird erstellt...' : 'Ticket aus diesem Eintrag erstellen'}
              </button>
              <button onClick={handleNewEntry}
                className="w-full flex items-center justify-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium px-4 py-3 rounded-xl transition-colors">
                <Plus size={16} />
                Weiteren Eintrag erstellen
              </button>
              <button onClick={onClose}
                className="w-full text-sm text-gray-400 hover:text-gray-600 py-2 transition-colors">
                Schliessen
              </button>
            </div>
          )}
        </div>

        {!savedEntry && (
          <div className="p-4 border-t border-gray-100">
            <button onClick={handleSave} disabled={saving}
              className="w-full bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-4 py-3 rounded-xl transition-colors disabled:opacity-50">
              {saving ? 'Wird gespeichert...' : 'Eintrag speichern'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');
  const [customersOpen, setCustomersOpen] = useState(false);
  const [suppliersOpen, setSuppliersOpen] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [journalOpen, setJournalOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    toast.success('Erfolgreich abgemeldet');
    navigate('/login');
  };

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      localStorage.setItem('sidebarCollapsed', !prev);
      return !prev;
    });
  };

  const toggleCustomers = useCallback(async () => {
    if (!customersOpen && customers.length === 0) {
      setLoadingCustomers(true);
      try {
        const res = await api.get('/customers?limit=200');
        setCustomers(res.data.customers || res.data || []);
      } catch (e) {
        console.error('Kunden laden fehlgeschlagen', e);
      } finally {
        setLoadingCustomers(false);
      }
    }
    setCustomersOpen(prev => !prev);
  }, [customersOpen, customers.length]);

  useEffect(() => {
    if (!suppliersOpen || suppliers.length > 0) return;
    setLoadingSuppliers(true);
    api.get('/suppliers?limit=200')
      .then(res => setSuppliers((res.data.suppliers || []).filter(s => s.is_active)))
      .catch(e => console.error('Lieferanten laden fehlgeschlagen', e))
      .finally(() => setLoadingSuppliers(false));
  }, [suppliersOpen, suppliers.length]);

  const navItems = [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/tickets?status=open', icon: Ticket, label: 'Tickets' },
    { to: '/mail', icon: Mail, label: 'Posteingang' },
    { to: '/journal', icon: BookOpen, label: 'Journal' },
    { to: '/devices', icon: Monitor, label: 'Geraete' },
    { to: '/serviceberichte', icon: FileText, label: 'Serviceberichte' },
    { to: '/dr-reports', icon: ShieldCheck, label: 'DR-Berichte' },
    { to: '/settings', icon: Settings, label: 'Einstellungen' },
    ...(user?.role === 'admin' ? [{ to: '/users', icon: Users, label: 'Benutzerverwaltung' }] : []),
  ];

  const isCustomersActive = location.pathname.startsWith('/customers') || location.search.includes('customer_id');
  const isSuppliersActive = location.pathname.startsWith('/suppliers') || location.search.includes('supplier_id');

  const SidebarContent = ({ isCollapsed }) => (
    <div className="flex flex-col h-full">
      <div className={'p-4 border-b border-white/10 flex items-center ' + (isCollapsed ? 'justify-center' : 'justify-between')}>
        {!isCollapsed && (
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.location.href = '/'}>
            <img src="/logo.png" alt="Emge Computer" className="h-8 w-8 object-contain flex-shrink-0" />
            <div>
              <h1 className="text-white font-bold text-lg tracking-tight">Emge Computer</h1>
              <p className="text-sidebar-text text-xs mt-0.5">Portal</p>
            </div>
          </div>
        )}
        <button onClick={toggleCollapsed}
          className="text-sidebar-text hover:text-white transition-colors p-1 rounded-lg hover:bg-sidebar-hover hidden md:block">
          {isCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>
      </div>

      {/* Journal Button */}
      <div className={'px-2 pt-3 pb-1'}>
        <button
          onClick={() => setJournalOpen(true)}
          title={isCollapsed ? '+ Journal' : undefined}
          className={'w-full flex items-center rounded-xl text-sm font-medium transition-colors bg-white/10 hover:bg-white/20 text-white ' +
            (isCollapsed ? 'justify-center px-2 py-2.5' : 'gap-2 px-3 py-2.5')}>
          <BookOpen size={16} className="shrink-0" />
          {!isCollapsed && <span>+ Journal</span>}
        </button>
      </div>

      <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
        {navItems.map(({ to, icon: Icon, label }) => (
          <NavLink key={to} to={to} end={to === '/'}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              'flex items-center rounded-xl text-sm font-medium transition-colors ' +
              (isCollapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5') +
              ' ' + (isActive ? 'bg-sidebar-active text-white' : 'text-sidebar-text hover:bg-sidebar-hover hover:text-white')
            }
            title={isCollapsed ? label : undefined}
          >
            <Icon size={18} className="shrink-0" />
            {!isCollapsed && label}
          </NavLink>
        ))}

        <div>
          <button
            onClick={isCollapsed ? () => { setCollapsed(false); localStorage.setItem('sidebarCollapsed', false); } : toggleCustomers}
            className={'w-full flex items-center rounded-xl text-sm font-medium transition-colors ' +
              (isCollapsed ? 'justify-center px-2 py-2.5' : 'justify-between gap-3 px-3 py-2.5') +
              ' ' + (isCustomersActive ? 'bg-sidebar-active text-white' : 'text-sidebar-text hover:bg-sidebar-hover hover:text-white')}
            title={isCollapsed ? 'Kunden' : undefined}>
            <span className={'flex items-center ' + (isCollapsed ? '' : 'gap-3')}>
              <Users size={18} className="shrink-0" />
              {!isCollapsed && 'Kunden'}
            </span>
            {!isCollapsed && (customersOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />)}
          </button>
          {!isCollapsed && customersOpen && (
            <div className="mt-1 ml-3 border-l border-white/20 pl-3 space-y-0.5">
              <Link to="/customers" onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-medium text-sidebar-text hover:text-white hover:bg-sidebar-hover transition-colors">
                Alle Kunden verwalten
              </Link>
              {loadingCustomers && <p className="px-2 py-1.5 text-xs text-sidebar-text opacity-60">Laden...</p>}
              {customers.map(c => (
                <Link key={c.id} to={'/customers/' + c.id}
                  onClick={() => setMobileOpen(false)}
                  className={'flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ' +
                    (location.pathname === '/customers/' + c.id ? 'bg-sidebar-active text-white' : 'text-sidebar-text hover:text-white hover:bg-sidebar-hover')}>
                  <Building2 size={13} className="flex-shrink-0 opacity-60" />
                  <span className="truncate">{c.name}</span>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div>
          <button
            onClick={isCollapsed ? () => { setCollapsed(false); localStorage.setItem('sidebarCollapsed', false); } : () => setSuppliersOpen(o => !o)}
            className={'w-full flex items-center rounded-xl text-sm font-medium transition-colors ' +
              (isCollapsed ? 'justify-center px-2 py-2.5' : 'justify-between gap-3 px-3 py-2.5') +
              ' ' + (isSuppliersActive ? 'bg-sidebar-active text-white' : 'text-sidebar-text hover:bg-sidebar-hover hover:text-white')}
            title={isCollapsed ? 'Lieferanten' : undefined}>
            <span className={'flex items-center ' + (isCollapsed ? '' : 'gap-3')}>
              <Building2 size={18} className="shrink-0" />
              {!isCollapsed && 'Lieferanten'}
            </span>
            {!isCollapsed && (suppliersOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />)}
          </button>
          {!isCollapsed && suppliersOpen && (
            <div className="mt-1 ml-3 border-l border-white/20 pl-3 space-y-0.5">
              <Link to="/suppliers" onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-medium text-sidebar-text hover:text-white hover:bg-sidebar-hover transition-colors">
                Alle Lieferanten verwalten
              </Link>
              {loadingSuppliers && <p className="px-2 py-1.5 text-xs text-sidebar-text opacity-60">Laden...</p>}
              {suppliers.map(s => (
                <Link key={s.id} to={'/tickets?supplier_id=' + s.id + '&supplier_name=' + encodeURIComponent(s.name)}
                  onClick={() => setMobileOpen(false)}
                  className={'flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ' +
                    (location.search.includes('supplier_id=' + s.id) ? 'bg-sidebar-active text-white' : 'text-sidebar-text hover:text-white hover:bg-sidebar-hover')}>
                  <Building2 size={13} className="flex-shrink-0 opacity-60" />
                  <span className="truncate">{s.name}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </nav>

      <div className={'p-2 border-t border-white/10 ' + (isCollapsed ? 'flex flex-col items-center gap-1' : '')}>
        {!isCollapsed && (
          <div className="px-3 py-2 mb-1">
            <p className="text-white text-sm font-medium">{user?.first_name} {user?.last_name}</p>
            <p className="text-xs text-sidebar-text">{user?.role === 'admin' ? 'Administrator' : 'Techniker'}</p>
          </div>
        )}
        <button onClick={handleLogout} title={isCollapsed ? 'Abmelden' : undefined}
          className={'flex items-center rounded-lg text-sm font-medium text-sidebar-text hover:bg-sidebar-hover hover:text-white transition-all ' +
            (isCollapsed ? 'justify-center p-2.5' : 'gap-3 px-3 py-2.5 w-full')}>
          <LogOut size={18} />
          {!isCollapsed && 'Abmelden'}
        </button>
      </div>
    </div>
  );

  const sidebarWidth = collapsed ? 'w-16' : 'w-60';
  const contentMargin = collapsed ? 'md:ml-16' : 'md:ml-60';

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <aside className={'hidden md:flex bg-sidebar flex-col fixed h-full z-20 transition-all duration-200 ' + sidebarWidth}>
        <SidebarContent isCollapsed={collapsed} />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-30 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-60 bg-sidebar flex flex-col z-40">
            <SidebarContent isCollapsed={false} />
          </aside>
        </div>
      )}

      <div className={'flex-1 min-w-0 flex flex-col min-h-screen transition-all duration-200 ' + contentMargin}>
        <header className="md:hidden bg-sidebar px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
          <button onClick={() => setMobileOpen(true)} className="text-white p-1">
            <Menu size={22} />
          </button>
          <h1 className="text-white font-bold text-base">Emge Computer</h1>
        </header>

        <main className="flex-1 p-4 md:p-6">
          {children}
        </main>
      </div>

      {journalOpen && <JournalPanel onClose={() => setJournalOpen(false)} />}
    </div>
  );
}
